import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Rocket, Clock } from 'lucide-react';

const Register = () => {
  // Countdown to launch date - June 25, 2026
  const launchDate = useMemo(() => new Date('2026-06-25T00:00:00'), []);
  
  const calculateTimeLeft = () => {
    const now = new Date();
    const difference = launchDate.getTime() - now.getTime();
    
    if (difference > 0) {
      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60)
      };
    }
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, [launchDate]);

  const TimeBlock = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/5 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/10">
        <span className="text-2xl sm:text-3xl font-bold text-white">
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <span className="text-xs sm:text-sm text-white/50 mt-2 uppercase tracking-wider">
        {label}
      </span>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A12] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="orb orb-brand w-[600px] h-[600px] -top-[200px] -right-[200px] animate-float-slow"
          style={{ opacity: 0.4 }}
        />
        <div 
          className="orb orb-lavender w-[500px] h-[500px] -bottom-[150px] -left-[150px] animate-float-slower"
          style={{ opacity: 0.3, animationDelay: '2s' }}
        />
        <div 
          className="orb orb-lime w-[300px] h-[300px] top-1/3 left-1/4 animate-float-slow"
          style={{ opacity: 0.15, animationDelay: '3s' }}
        />
      </div>

      <div className="w-full max-w-lg relative z-10 animate-fade-in-up">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <img 
              src="/logo-dark.png" 
              alt="FASCINAI ONE" 
              className="h-14 object-contain"
            />
          </div>
        </div>

        {/* Coming Soon Card - Glass Effect */}
        <div className="glass-dark rounded-3xl p-8 text-center" data-testid="maintenance-register-card">
          <div className="w-20 h-20 bg-brand-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Rocket className="w-10 h-10 text-brand-400" />
          </div>
          
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
            Próximamente
          </h2>
          
          <p className="text-white/60 mb-8">
            Estamos preparando algo increíble para ti. El registro estará disponible muy pronto.
          </p>

          {/* Countdown Timer */}
          <div className="mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Clock className="w-4 h-4 text-lime-400" />
              <span className="text-sm text-lime-400 font-medium">Lanzamiento en:</span>
            </div>
            <div className="flex items-center justify-center gap-3 sm:gap-4">
              <TimeBlock value={timeLeft.days} label="Días" />
              <span className="text-2xl text-white/30 font-light">:</span>
              <TimeBlock value={timeLeft.hours} label="Horas" />
              <span className="text-2xl text-white/30 font-light">:</span>
              <TimeBlock value={timeLeft.minutes} label="Min" />
              <span className="text-2xl text-white/30 font-light">:</span>
              <TimeBlock value={timeLeft.seconds} label="Seg" />
            </div>
          </div>

          {/* Explore Tools CTA */}
          <div className="bg-white/5 rounded-2xl p-4 mb-6 border border-white/10">
            <p className="text-white/70 text-sm mb-3">
              Mientras tanto, prueba nuestras herramientas gratuitas:
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link 
                to="/quiz"
                className="px-4 py-2 bg-brand-500/20 hover:bg-brand-500/30 text-brand-400 rounded-xl text-sm font-medium transition-colors border border-brand-500/30"
              >
                Quiz de Estrategia
              </Link>
              <Link 
                to="/auditor"
                className="px-4 py-2 bg-lime-500/20 hover:bg-lime-500/30 text-lime-400 rounded-xl text-sm font-medium transition-colors border border-lime-500/30"
              >
                Auditor de Marketing
              </Link>
            </div>
          </div>
          
          <div className="pt-4 border-t border-white/10">
            <p className="text-white/50 text-sm">
              ¿Ya tienes una cuenta de administrador?{' '}
              <Link 
                to="/login" 
                className="text-lime-400 hover:text-lime-300 font-medium transition-colors"
              >
                Inicia sesión
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-white/30 text-sm mt-8">
          © 2024 FASCINAI ONE. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
};

export default Register;
